"use client";
import { Box, Button, Drawer, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import Slider from "react-slick";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import { ResponseObject, Subscription } from "@/app/classes/main";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  paymentGradient,
  paymentPrimaryButtonSx,
} from "../cart/payment/paymentUi";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.css";
import Login from "../login/Login";
import Cookies from "js-cookie";
import ThankYouDialog from "./ThankYouDialog";
import { Loader } from "../genericComponents/MainLoader/Loader";

interface PlansPaymentsProps {
  openPlans: any;
  closePlans: any;
  reloadFlagAfterSubsciption: any;
}

export const PlansPayments: React.FC<PlansPaymentsProps> = ({
  openPlans,
  closePlans,
  reloadFlagAfterSubsciption,
}) => {
  var settings = {
    infinite: true,
    speed: 1000,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    className: "plansCarousel",
    autoplay: true,
    autoplaySpeed: 5000,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const [subscriptionData, setSubscriptionData] = useState<
    Subscription[] | undefined
  >(undefined);
  const [showLoginBox, setShowLoginBox] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const apiService = useMemo(() => new ApiService(), []);
  const token = getToken();
  const currencyCode = useCurrencyCode();
  const xHubId = getHubId();

  const getPlansData = useCallback(async () => {
    if (token) {
      try {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.subscriptionsWithLogin + "x-hubId=" + xHubId,
            token,
            xHubId
          );
        setSubscriptionData(responseObject?.object);
      } catch (error) {
        console.log("Error:", error);
      }
    } else {
      try {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.subscriptionsWithoutLogin,
            undefined,
            xHubId
          );
        setSubscriptionData(responseObject?.object);
      } catch (error) {
        console.log("Error:", error);
      }
    }
  }, [apiService, token, xHubId]);

  useEffect(() => {
    getPlansData();
  }, [getPlansData]);

  function handleUserLoggedIn() {
    localStorage.setItem("isAlreadyLogin", "true");
    Cookies.set("isAlreadyLogin", "true", {
      expires: 365,
      secure: true,
      sameSite: "Strict", // or 'Lax', depending on your use case
    });
    // setAlreadyLogin(true);
    window.location.reload();
  }

  const getSubscriptionApply = async (response: any, subscriptionId: any) => {
    try {
      const payload = {
        subscriptionId: subscriptionId,
        razorpayValidatePayment: {
          orderId: response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        },
        serviceIds: [],
        buildNumber: 100,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.subscribedCustomer,
        payload,
        token!,
        xHubId
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        setOpenDialog(true);
        reloadFlagAfterSubsciption(true);
      } else {
        alert("Payment verification failed.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
    }
  };

  const validatePayment = async (
    response: any,
    amount: number,
    subscriptionId: any
  ) => {
    try {
      const payload = {
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        razorpayOrder: {
          amount: amount,
          currency: "INR",
          id: response.razorpay_order_id,
          status: "captured",
        },
        signature: response.razorpay_signature,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.razorpayValidation,
        payload,
        token!
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        getSubscriptionApply(response, subscriptionId);
        setLoading(false);
        closePlans();
      } else {
        alert("Payment verification failed.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
      setLoading(false);
    }
  };

  const handlePayment = (
    e: any,
    orderId: string,
    amount: number,
    subscriptionId: any
  ) => {
    setLoading(false);
    const url =
      "https://play-lh.googleusercontent.com/Xfvj857cLrYU8GpQczqQYldDIPXYk2ow-bCuFVWQ633NmcKKx0iZ_hC5EQM-VbXC-y97"; // Optional

    const options = {
      key: "rzp_live_OwSnlsNKdctwe8", // Replace with your Key ID--
      amount: amount,
      currency: "INR",
      name: "Salon Tym",
      description: "Subscription Amount",
      image: url,
      order_id: orderId,
      handler: async (response: any) => {
        setLoading(true);
        await validatePayment(response, amount, subscriptionId);
      },
      // prefill: {
      //   name: "Pankaj Kumar",
      //   email: "pankajsing555@gmail.com",
      //   contact: "8384064378",
      // },
      theme: {
        color: "#15741a",
      },
    };
    const paymentObject = new (window as any).Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
      alert(`Payment Failed: ${response.error.description}`);
    });

    paymentObject.open();
    e.preventDefault();
  };

  const loadRazorpaySDK = (
    orderId: string,
    amount: number,
    subscriptionId: any
  ) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = (e) => handlePayment(e, orderId, amount, subscriptionId);
    document.body.appendChild(script);
  };

  const handleCreateOrder = async (item: any) => {
    setLoading(true);
    try {
      const payload = {
        amount: item?.amountForCashback * 100,
        currency: "INR",
        receipt: "string",
      };
      const responseObject = await apiService.post<ResponseObject>(
        apiService.razorpayCreateOrder,
        payload,
        token!
      );
      if (responseObject?.status === "success") {
        const { id: orderId } = await responseObject?.object;
        loadRazorpaySDK(orderId, payload?.amount, item?.id);
      } else {
        setLoading(false);
        alert("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      setLoading(false);
      alert("Something went wrong while verifying the payment.");
    }
  };

  return (
    <>
      <Drawer
        open={openPlans}
        onClose={closePlans}
        anchor="bottom"
        sx={{
          zIndex: 10000,
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: "22px",
            borderTopRightRadius: "22px",
            width: "100%",
            maxWidth: "100%",
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${alpha(PAYMENT_MINT, 0.14)}`,
            boxShadow: `0 -16px 48px ${alpha(PAYMENT_MINT_DARK, 0.16)}`,
          },
        }}
        PaperProps={{
          sx: {
            height: "auto",
            background: "transparent",
          },
        }}
        role="presentation"
      >
        {loading && <Loader />}
        <Box
          sx={{
            background: paymentGradient,
            px: { xs: 2, sm: 2.5 },
            pt: 1.25,
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.25 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 999,
                bgcolor: alpha("#fff", 0.35),
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <KeyboardBackspaceIcon
              sx={{
                background: alpha("#fff", 0.95),
                borderRadius: "12px",
                padding: "6px",
                fontSize: "26px",
                cursor: "pointer",
                color: "#0b1710",
                boxShadow: `0 4px 14px ${alpha("#000", 0.12)}`,
                flexShrink: 0,
              }}
              onClick={closePlans}
              aria-label="Close plans"
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                className="choose-plan-title playball-regular"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                  lineHeight: 1.25,
                }}
              >
                Choose your plan
              </Typography>
              <Typography
                variant="caption"
                className="lexend-heading"
                sx={{
                  color: alpha("#fff", 0.9),
                  display: "block",
                  mt: 0.5,
                  lineHeight: 1.45,
                }}
              >
                Membership perks and savings on every visit.
              </Typography>
            </Box>
          </Box>
        </Box>

        {subscriptionData && subscriptionData?.length > 0 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              bgcolor: "#fafcfb",
              px: { xs: 1.5, sm: 2 },
              py: 2,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                background: alpha(PAYMENT_MINT, 0.28),
              },
            }}
          >
            <Grid container spacing={0} sx={{ margin: "auto", maxWidth: "100%" }}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    textAlign: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                <Box sx={{ margin: "0 auto", width: "100%" }}>
                  {subscriptionData && subscriptionData.length == 1 ? (
                    <>
                      {subscriptionData &&
                        subscriptionData.length > 0 &&
                        subscriptionData?.map((item, i) => (
                          <Box
                            className="sliderContent"
                            key={i}
                            sx={{
                              margin: "auto",
                              bgcolor: "#fff",
                              borderRadius: "18px",
                              border: `1px solid ${alpha(PAYMENT_MINT, 0.12)}`,
                              p: 2,
                              boxShadow: `0 8px 28px ${alpha(PAYMENT_MINT_DARK, 0.07)}`,
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "left",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                src={item?.imageFile?.secureHttpPath}
                                alt="Logo"
                                style={{
                                  width: "40px",
                                  height: "auto",
                                }}
                                height={500}
                                width={500}
                              />
                              &nbsp; &nbsp;
                              <Typography
                                sx={{ fontWeight: "bold" }}
                                variant="h5"
                              >
                                {item?.heading}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontFamily: "Maestrale Pro",
                                fontSize: { xs: "14px", sm: "16px" },
                                margin: "8px 0px",
                                textAlign: "left",
                              }}
                              variant="body2"
                            >
                              {item?.subheading}
                            </Typography>
                            {item?.descriptions?.map(
                              (nesteditem: any, i: any) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "left",
                                    alignItems: "center",
                                  }}
                                >
                                  <CheckIcon
                                    fontSize="small"
                                    sx={{
                                      color: PAYMENT_MINT,
                                      paddingRight: "5px",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      textAlign: "left",
                                      fontSize: { xs: "12px", sm: "13px" },
                                    }}
                                    variant="body2"
                                  >
                                    {nesteditem}
                                  </Typography>
                                </Box>
                              )
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                margin: "16px 0px 8px",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                  }}
                                  variant="body1"
                                >
                                  {currencyCode}
                                  {item?.amountForCashback}
                                </Typography>
                                &nbsp;&nbsp;
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textDecoration: "line-through",
                                  }}
                                  variant="caption"
                                >
                                  {currencyCode}
                                  {item?.subscriptionMrp}
                                </Typography>
                                &nbsp;&nbsp;
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                  }}
                                  variant="body1"
                                >
                                  <span
                                    style={{
                                      fontWeight: "normal",
                                      fontSize: "14px",
                                      marginRight: "5px",
                                    }}
                                  >
                                    Save
                                  </span>
                                  {currencyCode}
                                  {item?.subscriptionDiscount}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                sx={{
                                  ...paymentPrimaryButtonSx,
                                  py: 0.75,
                                  px: 2.5,
                                  minHeight: 40,
                                  fontSize: "0.875rem",
                                }}
                                onClick={
                                  token
                                    ? () => handleCreateOrder(item)
                                    : () => setShowLoginBox(true)
                                }
                              >
                                Subscribe
                              </Button>
                            </Box>
                          </Box>
                        ))}
                    </>
                  ) : (
                    <Slider {...settings}>
                      {subscriptionData &&
                        subscriptionData.length > 0 &&
                        subscriptionData?.map((item, i) => (
                          <Box
                            className="sliderContent"
                            key={i}
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: "18px",
                              border: `1px solid ${alpha(PAYMENT_MINT, 0.12)}`,
                              p: 2,
                              mx: 1,
                              boxShadow: `0 8px 28px ${alpha(PAYMENT_MINT_DARK, 0.07)}`,
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "left",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                src={item?.imageFile?.secureHttpPath}
                                alt="Logo"
                                style={{
                                  width: "40px",
                                  height: "auto",
                                }}
                                height={500}
                                width={500}
                              />
                              &nbsp; &nbsp;
                              <Typography
                                sx={{ fontWeight: "bold" }}
                                variant="h5"
                              >
                                {item?.heading}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontFamily: "Maestrale Pro",
                                fontSize: { xs: "14px", sm: "16px" },
                                margin: "8px 0px",
                                textAlign: "left",
                              }}
                              variant="body2"
                            >
                              {item?.subheading}
                            </Typography>
                            {item?.descriptions?.map(
                              (nesteditem: any, i: any) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "left",
                                    alignItems: "center",
                                  }}
                                >
                                  <CheckIcon
                                    fontSize="small"
                                    sx={{
                                      color: PAYMENT_MINT,
                                      paddingRight: "5px",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      textAlign: "left",
                                      fontSize: { xs: "12px", sm: "13px" },
                                    }}
                                    variant="body2"
                                  >
                                    {nesteditem}
                                  </Typography>
                                </Box>
                              )
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                margin: "16px 0px 8px",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                  }}
                                  variant="body1"
                                >
                                  {currencyCode}
                                  {item?.amountForCashback}
                                </Typography>
                                &nbsp;&nbsp;
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textDecoration: "line-through",
                                  }}
                                  variant="caption"
                                >
                                  {currencyCode}
                                  {item?.subscriptionMrp}
                                </Typography>
                                &nbsp;&nbsp;
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                  }}
                                  variant="body1"
                                >
                                  <span
                                    style={{
                                      fontWeight: "normal",
                                      fontSize: "14px",
                                      marginRight: "5px",
                                    }}
                                  >
                                    Save
                                  </span>
                                  {currencyCode}
                                  {item?.subscriptionDiscount}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                sx={{
                                  ...paymentPrimaryButtonSx,
                                  py: 0.75,
                                  px: 2.5,
                                  minHeight: 40,
                                  fontSize: "0.875rem",
                                }}
                                onClick={
                                  token
                                    ? () => handleCreateOrder(item)
                                    : () => setShowLoginBox(true)
                                }
                              >
                                Subscribe
                              </Button>
                            </Box>
                          </Box>
                        ))}
                    </Slider>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        ) : null}
      </Drawer>
      <Login
        showLoginBox={showLoginBox}
        handleLoginClose={() => setShowLoginBox(false)}
        handleUserLoggedIn={handleUserLoggedIn}
      />
      <ThankYouDialog open={openDialog} onClose={() => setOpenDialog(false)} />
    </>
  );
};
